import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { isBefore, format, parseISO } from 'date-fns';

import { Typography } from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

import EditButton from '@ui-elements/EditButton';
import DeliveryDateArea from '@ui-parts/DeliveryDateArea';

import { callThunk, fetchDefaultDeliveryDate } from '~/api';
import { FONT_WEIGHTS } from '~/assets/styles/base';
import DeliveryDateModal from '../DeliveryDateModal';

const FlexBox = styled.div`
  display: flex;
  align-items: center;
`;

const ContentBoxTitle = styled(Typography)`
  font-weight: ${FONT_WEIGHTS.size2};
`;

const ContentBoxDesc = styled(Typography)`
  margin-left: 25px;
`;

const ContentsWrapper = styled.div`
  margin: 20px 0 0;
`;

const DeliveryDateAreaWrapper = styled.div`
  display: flex;
`;

const EditButtonContainer = styled.div`
  margin-left: 26px;
  min-width: 66px;
  width: 66px;
`;

function DeliveryDate({ canEdit = false }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    survey: { deliveryDate: storeDeliveryDate },
  } = useSelector((state) => state);

  const [openModal, setOpenModal] = useState(false);
  const [minDeliveryDate, setMinDeliveryDate] = useState(null);
  const [defaultDeliveryDate, setDefaultDeliveryDate] = useState(null);
  const [forbiddenSchedules, setForbiddenSchedules] = useState([]);

  useEffect(() => {
    // 最小の配信可能日をAPI経由で取得しlocal stateにセット
    (async () => {
      const {
        payload: {
          availableDeliveryStartDate,
          defaultDeliveryStartDate,
          forbiddenSchedules: resForbiddenSchedules,
        },
      } = await dispatch(
        callThunk({
          action: fetchDefaultDeliveryDate,
        })
      );
      setMinDeliveryDate(availableDeliveryStartDate);
      setDefaultDeliveryDate(defaultDeliveryStartDate);

      // forbiddenSchedulesをDateにしておく。
      let forbiddenDates = [];
      if (resForbiddenSchedules.length > 0) {
        forbiddenDates = resForbiddenSchedules.map((ele) => ({
          startTime: parseISO(ele.startTime),
          endTime: parseISO(ele.endTime),
        }));
      }
      setForbiddenSchedules(forbiddenDates);
    })();
  }, []);

  /**
   * 編集可能な時だけ、storeの配信開始日と配信開始可能日を比較して、
   * 配信開始日が配信開始可能日より前なら配信開始可能日を配信開始日とする。
   */
  useEffect(() => {
    if (canEdit && minDeliveryDate) {
      if (!storeDeliveryDate || isBefore(parseISO(storeDeliveryDate), parseISO(minDeliveryDate))) {
        dispatch({
          type: 'survey/updated',
          payload: {
            deliveryDate: defaultDeliveryDate,
          },
        });
      }
    }
  }, [storeDeliveryDate, minDeliveryDate, canEdit]);

  return (
    <>
      <FlexBox>
        <ContentBoxTitle variant="title2">{t('survey.deliveryDateTime')}</ContentBoxTitle>
        {canEdit && minDeliveryDate && storeDeliveryDate && (
          <ContentBoxDesc variant="title3">
            {t('survey.availableDeliveryStartDate', {
              date: format(parseISO(minDeliveryDate), 'yyyy/MM/dd HH:mm'),
            })}
          </ContentBoxDesc>
        )}
      </FlexBox>
      <ContentsWrapper>
        <DeliveryDateAreaWrapper>
          {minDeliveryDate && storeDeliveryDate && (
            <>
              <DeliveryDateArea isoDeliveryDate={storeDeliveryDate} canEdit={canEdit} />
              {canEdit && (
                <EditButtonContainer>
                  <EditButton
                    name="DeliveryDate"
                    sx={{ width: 148 }}
                    handleClick={() => {
                      setOpenModal(true);
                    }}
                  >
                    <EditRoundedIcon />
                  </EditButton>
                </EditButtonContainer>
              )}
            </>
          )}
        </DeliveryDateAreaWrapper>
      </ContentsWrapper>

      {canEdit && minDeliveryDate && storeDeliveryDate && (
        <DeliveryDateModal
          open={openModal}
          handleClose={() => {
            setOpenModal(false);
          }}
          isoDeliveryDate={storeDeliveryDate}
          minDeliveryDate={minDeliveryDate}
          forbiddenSchedules={forbiddenSchedules}
        />
      )}
    </>
  );
}

DeliveryDate.propTypes = {
  canEdit: PropTypes.bool,
};

export default DeliveryDate;
